import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../AuthContext';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import 'xterm/css/xterm.css';
import { LuServer, LuRefreshCw, LuPower, LuPlay, LuSquare, LuCpu, LuUsers, LuClock, LuActivity, LuTerminal, LuX, LuCopy, LuCheck, LuLock } from 'react-icons/lu';

function AdminNodesPage() {
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [restartingNode, setRestartingNode] = useState(null);
  const [restartingAll, setRestartingAll] = useState(false);
  const [enablingNode, setEnablingNode] = useState(null);
  const [disablingNode, setDisablingNode] = useState(null);
  const [sshModal, setSshModal] = useState({ open: false, node: null, step: 'password' });
  const [sshPassword, setSshPassword] = useState('');
  const [sshConnecting, setSshConnecting] = useState(false);
  const [sshError, setSshError] = useState('');
  const [sshSessionId, setSshSessionId] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const { token } = useAuth();
  
  const terminalRef = useRef(null);
  const terminalContainerRef = useRef(null);
  const fitAddonRef = useRef(null);
  const readIntervalRef = useRef(null);

  const fetchNodes = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const response = await fetch('/api/admin/nodes', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Не удалось загрузить ноды');
      }
      const data = await response.json();
      const nodesList = Array.isArray(data) ? data : [];
      setNodes(nodesList);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchNodes();
    }
  }, [token, fetchNodes]);

  // Автообновление каждые 30 секунд
  useEffect(() => {
    const interval = setInterval(() => {
      if (token && !refreshing && !sshModal.open) {
        fetchNodes();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [token, refreshing, fetchNodes, sshModal.open]);

  const sendToSSH = useCallback(async (data) => {
    if (!sshSessionId) return;
    
    try {
      await fetch('/api/admin/ssh/send', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          session_id: sshSessionId,
          data: data
        })
      });
    } catch (e) {
      console.error('SSH send error:', e);
    }
  }, [sshSessionId, token]);

  const resizeSSH = useCallback(async () => {
    if (!sshSessionId || !terminalRef.current) return;
    
    try {
      await fetch('/api/admin/ssh/resize', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          session_id: sshSessionId,
          cols: terminalRef.current.cols,
          rows: terminalRef.current.rows
        })
      });
    } catch (e) {
      console.error('SSH resize error:', e);
    }
  }, [sshSessionId, token]);

  const startReading = useCallback(() => {
    if (readIntervalRef.current) {
      clearInterval(readIntervalRef.current);
    }
    
    readIntervalRef.current = setInterval(async () => {
      if (!sshSessionId) return;
      
      try {
        const response = await fetch('/api/admin/ssh/read', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ session_id: sshSessionId })
        });
        
        const data = await response.json();
        
        if (data.output && terminalRef.current) {
          terminalRef.current.write(data.output);
        }
        
        if (data.disconnected) {
          if (terminalRef.current) {
            terminalRef.current.write('\r\n\x1b[31m--- Соединение закрыто ---\x1b[0m\r\n');
          }
          clearInterval(readIntervalRef.current);
        }
      } catch (e) {
        console.error('SSH read error:', e);
      }
    }, 100);
  }, [sshSessionId, token]);

  // Инициализация терминала
  useEffect(() => {
    if (sshModal.step === 'terminal' && terminalContainerRef.current && !terminalRef.current) {
      const term = new Terminal({
        cursorBlink: true,
        fontSize: 14,
        fontFamily: '"Fira Code", "JetBrains Mono", "Cascadia Code", Menlo, Monaco, "Courier New", monospace',
        theme: {
          background: '#0d1117',
          foreground: '#c9d1d9',
          cursor: '#58a6ff',
          cursorAccent: '#0d1117',
          selectionBackground: '#264f78',
          black: '#484f58',
          red: '#ff7b72',
          green: '#3fb950',
          yellow: '#d29922',
          blue: '#58a6ff',
          magenta: '#bc8cff',
          cyan: '#39c5cf',
          white: '#b1bac4',
          brightBlack: '#6e7681',
          brightRed: '#ffa198',
          brightGreen: '#56d364',
          brightYellow: '#e3b341',
          brightBlue: '#79c0ff',
          brightMagenta: '#d2a8ff',
          brightCyan: '#56d4dd',
          brightWhite: '#f0f6fc',
        },
        scrollback: 5000,
        convertEol: true,
      });
      
      const fitAddon = new FitAddon();
      term.loadAddon(fitAddon);
      
      term.open(terminalContainerRef.current);
      fitAddon.fit();
      
      terminalRef.current = term;
      fitAddonRef.current = fitAddon;
      
      // Обработка ввода
      term.onData((data) => {
        sendToSSH(data);
      });
      
      // Resize при изменении окна
      const handleResize = () => {
        if (fitAddonRef.current) {
          fitAddonRef.current.fit();
          resizeSSH();
        }
      };
      window.addEventListener('resize', handleResize);
      
      // Начинаем читать данные
      startReading();
      
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [sshModal.step, sendToSSH, resizeSSH, startReading]);

  // Очистка при закрытии
  useEffect(() => {
    return () => {
      if (readIntervalRef.current) {
        clearInterval(readIntervalRef.current);
      }
      if (terminalRef.current) {
        terminalRef.current.dispose();
        terminalRef.current = null;
      }
    };
  }, []);

  const handleSSHConnect = async () => {
    if (!sshPassword || !sshModal.node) return;
    
    setSshConnecting(true);
    setSshError('');
    
    try {
      const response = await fetch('/api/admin/ssh/connect', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          host: sshModal.node.address,
          port: 22,
          username: 'root',
          password: sshPassword
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Ошибка подключения');
      }
      
      setSshSessionId(data.session_id);
      setSshModal({ ...sshModal, step: 'terminal' });
      
    } catch (e) {
      setSshError(e.message);
    } finally {
      setSshConnecting(false);
    }
  };

  const handleSSHDisconnect = async () => {
    if (readIntervalRef.current) {
      clearInterval(readIntervalRef.current);
    }
    
    if (sshSessionId) {
      try {
        await fetch('/api/admin/ssh/disconnect', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ session_id: sshSessionId })
        });
      } catch (e) {
        console.error('Disconnect error:', e);
      }
    }
    
    if (terminalRef.current) {
      terminalRef.current.dispose();
      terminalRef.current = null;
    }
    
    setSshSessionId(null);
    setSshPassword('');
    setSshError('');
    setSshModal({ open: false, node: null, step: 'password' });
  };

  const handleRefresh = () => fetchNodes();

  const handleRestartNode = async (uuid) => {
    if (!window.confirm(`Перезапустить ноду?`)) return;
    setRestartingNode(uuid);
    try {
      const response = await fetch(`/api/admin/nodes/${uuid}/restart`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Ошибка перезапуска');
      setTimeout(() => fetchNodes(), 2000);
    } catch (e) {
      alert(`Ошибка: ${e.message}`);
    } finally {
      setRestartingNode(null);
    }
  };

  const handleRestartAll = async () => {
    if (!window.confirm(`Перезапустить ВСЕ ноды?`)) return;
    setRestartingAll(true);
    try {
      const response = await fetch('/api/admin/nodes/restart-all', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Ошибка');
      setTimeout(() => fetchNodes(), 3000);
    } catch (e) {
      alert(`Ошибка: ${e.message}`);
    } finally {
      setRestartingAll(false);
    }
  };

  const handleEnableNode = async (uuid) => {
    setEnablingNode(uuid);
    try {
      await fetch(`/api/admin/nodes/${uuid}/enable`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTimeout(() => fetchNodes(), 1000);
    } catch (e) {
      alert(`Ошибка: ${e.message}`);
    } finally {
      setEnablingNode(null);
    }
  };

  const handleDisableNode = async (uuid) => {
    if (!window.confirm(`Отключить ноду?`)) return;
    setDisablingNode(uuid);
    try {
      await fetch(`/api/admin/nodes/${uuid}/disable`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTimeout(() => fetchNodes(), 1000);
    } catch (e) {
      alert(`Ошибка: ${e.message}`);
    } finally {
      setDisablingNode(null);
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatUptime = (seconds) => {
    if (!seconds) return 'N/A';
    const sec = parseInt(seconds);
    const days = Math.floor(sec / 86400);
    const hours = Math.floor((sec % 86400) / 3600);
    const mins = Math.floor((sec % 3600) / 60);
    if (days > 0) return `${days}д ${hours}ч`;
    if (hours > 0) return `${hours}ч ${mins}м`;
    return `${mins}м`;
  };

  const getCountryFlag = (code) => {
    if (!code) return '🌐';
    const flags = {
      'FR': '🇫🇷', 'DE': '🇩🇪', 'NL': '🇳🇱', 'US': '🇺🇸', 'GB': '🇬🇧',
      'UA': '🇺🇦', 'PL': '🇵🇱', 'FI': '🇫🇮', 'SE': '🇸🇪', 'NO': '🇳🇴',
      'RU': '🇷🇺', 'CA': '🇨🇦', 'JP': '🇯🇵', 'SG': '🇸🇬', 'AU': '🇦🇺',
      'KR': '🇰🇷', 'HK': '🇭🇰', 'TR': '🇹🇷', 'IT': '🇮🇹', 'ES': '🇪🇸'
    };
    return flags[code.toUpperCase()] || '🌐';
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (e) {}
  };

  const openSSHModal = (node) => {
    setSshModal({ open: true, node, step: 'password' });
    setSshPassword('');
    setSshError('');
  };

  const getNodeStatus = (node) => {
    if (node.isDisabled) return 'disabled';
    if (node.isConnecting) return 'connecting';
    if (node.isConnected) return 'online';
    return 'offline';
  };

  const getStatusInfo = (status) => {
    const statuses = {
      'online': { color: '#10b981', bg: 'rgba(16, 185, 129, 0.15)', label: 'Online' },
      'offline': { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)', label: 'Offline' },
      'disabled': { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.15)', label: 'Disabled' },
      'connecting': { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)', label: 'Connecting' }
    };
    return statuses[status] || statuses['offline'];
  };

  const stats = {
    total: nodes.length,
    online: nodes.filter(n => n.isConnected && !n.isDisabled).length,
    offline: nodes.filter(n => !n.isConnected && !n.isDisabled).length,
    disabled: nodes.filter(n => n.isDisabled).length,
    totalUsers: nodes.reduce((sum, n) => sum + (n.usersOnline || 0), 0),
    totalTraffic: nodes.reduce((sum, n) => sum + (n.trafficUsedBytes || 0), 0)
  };

  return (
    <main className="admin-page-content">
      <section>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <h2 style={{ color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
            <LuServer /> Управление нодами
          </h2>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button className="btn" onClick={handleRefresh} disabled={refreshing} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <LuRefreshCw style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
              Обновить
            </button>
            <button className="btn btn-danger" onClick={handleRestartAll} disabled={restartingAll} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <LuPower style={{ animation: restartingAll ? 'spin 1s linear infinite' : 'none' }} />
              {restartingAll ? 'Перезапуск...' : 'Перезапустить все'}
            </button>
          </div>
        </div>

        {/* Статистика */}
        {!loading && !error && nodes.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <div style={{ background: 'var(--surface-muted)', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)' }}>{stats.total}</div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>Всего</div>
            </div>
            <div style={{ background: 'rgba(16, 185, 129, 0.1)', borderRadius: '12px', padding: '16px', textAlign: 'center', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#10b981' }}>{stats.online}</div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>Online</div>
            </div>
            <div style={{ background: 'rgba(239, 68, 68, 0.1)', borderRadius: '12px', padding: '16px', textAlign: 'center', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#ef4444' }}>{stats.offline}</div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>Offline</div>
            </div>
            <div style={{ background: 'var(--surface-muted)', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--accent-primary)' }}>{stats.totalUsers}</div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>Юзеров</div>
            </div>
            <div style={{ background: 'var(--surface-muted)', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>{formatBytes(stats.totalTraffic)}</div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>Трафик</div>
            </div>
          </div>
        )}

        {loading && <div className="loading-mini">Загрузка нод...</div>}
        {error && <div className="message-error">{error}</div>}

        {!loading && !error && (
          <>
            {nodes.length === 0 ? (
              <div className="loading-mini">Ноды не найдены</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '20px' }}>
                {nodes.map((node, index) => {
                  const status = getNodeStatus(node);
                  const statusInfo = getStatusInfo(status);
                  
                  return (
                    <div key={node.uuid || index} style={{
                      background: 'var(--glass-bg)',
                      border: '1px solid var(--glass-border)',
                      borderRadius: '16px',
                      padding: '20px',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: statusInfo.color }} />
                      
                      {/* Header */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                          <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: statusInfo.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>
                            {getCountryFlag(node.countryCode)}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {node.name || 'Unnamed'}
                            </h3>
                            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                              {node.address}:{node.port}
                            </div>
                          </div>
                        </div>
                        <div style={{ padding: '4px 10px', borderRadius: '8px', background: statusInfo.bg, color: statusInfo.color, fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: statusInfo.color }} />
                          {statusInfo.label}
                        </div>
                      </div>
                      
                      {/* Metrics */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '16px' }}>
                        <div style={{ background: 'var(--surface-muted)', borderRadius: '10px', padding: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <LuUsers style={{ color: 'var(--accent-primary)', fontSize: '18px' }} />
                          <div>
                            <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)' }}>{node.usersOnline ?? 0}</div>
                            <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Онлайн</div>
                          </div>
                        </div>
                        <div style={{ background: 'var(--surface-muted)', borderRadius: '10px', padding: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <LuClock style={{ color: '#10b981', fontSize: '18px' }} />
                          <div>
                            <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)' }}>{formatUptime(node.xrayUptime)}</div>
                            <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Аптайм</div>
                          </div>
                        </div>
                        <div style={{ background: 'var(--surface-muted)', borderRadius: '10px', padding: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <LuActivity style={{ color: '#f59e0b', fontSize: '18px' }} />
                          <div>
                            <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)' }}>{formatBytes(node.trafficUsedBytes)}</div>
                            <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Трафик</div>
                          </div>
                        </div>
                        <div style={{ background: 'var(--surface-muted)', borderRadius: '10px', padding: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <LuCpu style={{ color: '#8b5cf6', fontSize: '18px' }} />
                          <div>
                            <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)' }}>{node.cpuCount ?? 'N/A'} / {node.totalRam || 'N/A'}</div>
                            <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>CPU / RAM</div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Details */}
                      <div style={{ background: 'var(--surface-muted)', borderRadius: '10px', padding: '10px 12px', marginBottom: '16px', fontSize: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ color: 'var(--text-secondary)' }}>Xray:</span>
                          <span style={{ color: 'var(--text-primary)' }}>{node.xrayVersion || 'N/A'}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: 'var(--text-secondary)' }}>Node:</span>
                          <span style={{ color: 'var(--text-primary)' }}>{node.nodeVersion || 'N/A'}</span>
                        </div>
                      </div>
                      
                      {/* Buttons */}
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => openSSHModal(node)} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--surface-muted)', color: 'var(--text-primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '13px', fontWeight: '600' }}>
                          <LuTerminal /> SSH
                        </button>
                        <button onClick={() => handleRestartNode(node.uuid)} disabled={restartingNode === node.uuid} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', background: 'var(--accent-primary)', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', opacity: restartingNode === node.uuid ? 0.6 : 1 }}>
                          <LuPower style={{ animation: restartingNode === node.uuid ? 'spin 1s linear infinite' : 'none' }} />
                          Restart
                        </button>
                        {node.isDisabled ? (
                          <button onClick={() => handleEnableNode(node.uuid)} disabled={enablingNode === node.uuid} style={{ padding: '10px 14px', borderRadius: '8px', border: 'none', background: '#10b981', color: 'white', cursor: 'pointer' }}>
                            <LuPlay />
                          </button>
                        ) : (
                          <button onClick={() => handleDisableNode(node.uuid)} disabled={disablingNode === node.uuid} style={{ padding: '10px 14px', borderRadius: '8px', border: 'none', background: '#ef4444', color: 'white', cursor: 'pointer' }}>
                            <LuSquare />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </section>

      {/* SSH Modal */}
      {sshModal.open && sshModal.node && (
        <div onClick={sshModal.step === 'password' ? handleSSHDisconnect : undefined} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10000, padding: '20px'
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: sshModal.step === 'terminal' ? '#0d1117' : 'var(--bg-primary)',
            borderRadius: '16px',
            width: sshModal.step === 'terminal' ? '95%' : '400px',
            maxWidth: sshModal.step === 'terminal' ? '1400px' : '400px',
            height: sshModal.step === 'terminal' ? '85vh' : 'auto',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '16px 20px',
              borderBottom: '1px solid rgba(255,255,255,0.1)',
              background: sshModal.step === 'terminal' ? '#161b22' : 'transparent'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '20px' }}>{getCountryFlag(sshModal.node.countryCode)}</span>
                <div>
                  <div style={{ fontWeight: '600', color: sshModal.step === 'terminal' ? '#c9d1d9' : 'var(--text-primary)' }}>
                    {sshModal.node.name}
                  </div>
                  <div style={{ fontSize: '12px', color: sshModal.step === 'terminal' ? '#8b949e' : 'var(--text-secondary)' }}>
                    root@{sshModal.node.address}
                  </div>
                </div>
              </div>
              <button onClick={handleSSHDisconnect} style={{
                background: 'rgba(239, 68, 68, 0.2)',
                border: 'none', color: '#ef4444',
                padding: '8px 12px', borderRadius: '8px',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
                fontSize: '13px', fontWeight: '600'
              }}>
                <LuX size={16} />
                {sshModal.step === 'terminal' ? 'Отключиться' : 'Закрыть'}
              </button>
            </div>
            
            {sshModal.step === 'password' ? (
              /* Password Step */
              <div style={{ padding: '24px' }}>
                <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                  <LuLock size={48} style={{ color: 'var(--accent-primary)', marginBottom: '12px' }} />
                  <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>SSH Подключение</h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: '8px 0 0' }}>
                    Введите пароль для root
                  </p>
                </div>
                
                {sshError && (
                  <div style={{
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '8px', padding: '12px',
                    color: '#ef4444', fontSize: '14px',
                    marginBottom: '16px', textAlign: 'center'
                  }}>
                    {sshError}
                  </div>
                )}
                
                <form onSubmit={(e) => { e.preventDefault(); handleSSHConnect(); }}>
                  <input
                    type="password"
                    value={sshPassword}
                    onChange={(e) => setSshPassword(e.target.value)}
                    placeholder="Пароль root"
                    autoFocus
                    style={{
                      width: '100%', padding: '14px 16px',
                      borderRadius: '10px',
                      border: '1px solid var(--border-color)',
                      background: 'var(--surface-muted)',
                      color: 'var(--text-primary)',
                      fontSize: '16px',
                      marginBottom: '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                  
                  <button type="submit" disabled={sshConnecting || !sshPassword} style={{
                    width: '100%', padding: '14px',
                    borderRadius: '10px', border: 'none',
                    background: 'linear-gradient(135deg, var(--accent-primary), #60a5fa)',
                    color: 'white', fontSize: '16px', fontWeight: '600',
                    cursor: sshConnecting ? 'not-allowed' : 'pointer',
                    opacity: sshConnecting || !sshPassword ? 0.6 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                  }}>
                    {sshConnecting ? (
                      <>
                        <LuRefreshCw style={{ animation: 'spin 1s linear infinite' }} />
                        Подключение...
                      </>
                    ) : (
                      <>
                        <LuTerminal />
                        Подключиться
                      </>
                    )}
                  </button>
                </form>
                
                <div style={{ marginTop: '20px', padding: '12px', background: 'var(--surface-muted)', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>IP:</span>
                    <button onClick={() => copyToClipboard(sshModal.node.address, 'ip')} style={{
                      background: 'transparent', border: 'none',
                      color: copiedField === 'ip' ? '#10b981' : 'var(--text-primary)',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
                      fontSize: '13px', fontFamily: 'monospace'
                    }}>
                      {sshModal.node.address}
                      {copiedField === 'ip' ? <LuCheck size={14} /> : <LuCopy size={14} />}
                    </button>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>User:</span>
                    <span style={{ fontSize: '13px', color: 'var(--text-primary)', fontFamily: 'monospace' }}>root</span>
                  </div>
                </div>
              </div>
            ) : (
              /* Terminal Step */
              <div ref={terminalContainerRef} style={{
                flex: 1, padding: '8px',
                background: '#0d1117',
                minHeight: '400px'
              }} />
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </main>
  );
}

export default AdminNodesPage;
